/**
 * Whole production units in the staff order editor — run 14 item 3.
 *
 * §6.7 step 5 rounds every line the customer page produces up to whole
 * production units, because that is how the kitchen makes food: a wrap is
 * cut into eight and there is no such thing as nine of it. The console's
 * quantity fields were plain numbers and took whatever was typed, so an
 * order could carry nine wrap rolls (Balázs, 14 October 2026).
 *
 * The rounding is measured on the **dish's total**, its main line and its
 * gluten-free line together, and never on either alone. Run 9 item 5 settled
 * that: the gluten-free box share is carved out of a batch the main line has
 * already rounded, so the two add up to whole units and neither is whole by
 * itself. `min_production` is measured the same way.
 *
 * Nothing here reads or writes a catalogue value: the unit is handed in.
 */

/** A production unit that can be divided by. Anything else is one. */
export const unitOrOne = (unit: number | null | undefined): number =>
  typeof unit === 'number' && Number.isFinite(unit) && unit >= 1 ? Math.floor(unit) : 1

/** The next whole number of units at or above `value`. Zero stays zero: a
 *  dish taken off the order is not rounded back on to it. */
export function roundUpToUnit(value: number, unit: number): number {
  const step = unitOrOne(unit)
  if (!Number.isFinite(value) || value <= 0) return 0
  return Math.ceil(value / step) * step
}

/** True where `value` is a whole number of units (zero included). */
export const isWholeUnits = (value: number, unit: number): boolean =>
  Number.isFinite(value) && value >= 0 && value % unitOrOne(unit) === 0

/**
 * One press of − or +. From a whole count it moves exactly one unit; from a
 * count that is not whole — one typed before this rule existed, or one still
 * being typed — it moves to the nearest whole count in that direction, so
 * the first press is what makes it whole. It never goes below zero.
 */
export function stepBy(value: number, unit: number, direction: 1 | -1): number {
  const step = unitOrOne(unit)
  const from = Number.isFinite(value) && value > 0 ? value : 0
  if (direction === 1) {
    return isWholeUnits(from, step) ? from + step : roundUpToUnit(from, step)
  }
  const down = isWholeUnits(from, step) ? from - step : Math.floor(from / step) * step
  return Math.max(0, down)
}

/**
 * What one of a dish's two quantity fields becomes when the dish's total has
 * to be a whole number of units. `other` is the dish's other line — the
 * gluten-free one beside a main one, or the other way round, and zero where
 * there is only one. The shortfall goes on the field that was edited, so the
 * figure the staff member typed is the one that moves and the one they did
 * not is left exactly as it is.
 */
export function wholeFieldValue(field: number, other: number, unit: number): number {
  const step = unitOrOne(unit)
  const mine = Number.isFinite(field) && field > 0 ? Math.round(field) : 0
  const theirs = Number.isFinite(other) && other > 0 ? Math.round(other) : 0
  if (mine === 0 && theirs === 0) return 0
  const total = mine + theirs
  if (isWholeUnits(total, step)) return mine
  return mine + (roundUpToUnit(total, step) - total)
}
